<template>
  <v-card>
    <v-card-title>
      <span class="text-h5">Импорт отчётной формы</span>
      <a class="close" @click="$emit('close')">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="#0033A0"/>
        </svg>
      </a>
    </v-card-title>

    <v-card-text>
      <v-form ref="form">
        <v-file-input
            v-model="file"
            label="Файл Excel для импорта"
            accept=".xlsx"
            prepend-icon="mdi-file-excel"
            show-size
            dense
            outlined
            hide-details
            clearable
            @change="onFileChange"
        />
      </v-form>

      <div class="instruction mt-4">
        <h3>Экспортируйте шаблон</h3>
        <ul class="mb-2">
          <li>Нажмите кнопку <b>«Экспорт шаблона»</b>.</li>
        </ul>

        <h3>Шапка таблицы</h3>
        <ul class="mb-2">
          <li>Строки <b>1–5</b> должны полностью совпадать с шаблоном.</li>
        </ul>

        <h3>Заполнение данных</h3>
        <ul class="mb-2">
          <li>Данные вносятся с <b>6-й строки</b>.</li>
          <li><b>Важно!</b> Не используйте объединённые ячейки.</li>
        </ul>

        <h3>Формат данных</h3>
        <ul class="mb-2">
          <li><b>Дата:</b> строго в формате <code>ДД.ММ.ГГГГ</code> (пример: 01.07.2025).</li>
        </ul>

        <h3>Групповые показатели</h3>
        <ul class="mb-2">
          <li>Указывайте сверху вниз в рамках одной группы.</li>
          <li>Запрещены пропуски строк внутри группы.</li>
        </ul>

        <h3>Дополнительные файлы</h3>
        <ul class="mb-2">
          <li>Файлы для показателей прикладываются <b>после импорта</b> отчётной формы через интерфейс системы.</li>
        </ul>
      </div>

      <v-btn small @click="exportTemplate" class="my-2">Экспорт шаблона</v-btn>

      <div v-if="result">
        <v-alert
            v-if="result.ok"
            type="success"
            dense
            text
        >
          {{ result.message }}
        </v-alert>

        <div v-if="result && !result.ok">
          <v-alert type="error" dense outlined class="mb-3">
            <div>{{ result.message }}</div>
          </v-alert>

          <v-simple-table dense v-if="result.errors && result.errors.length">
            <thead>
            <tr>
              <th class="text-left" style="max-width: 80px">Строка</th>
              <th class="text-left" style="max-width: 80px">Колонка</th>
              <th class="text-left">Сообщение</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(e, i) in visibleErrors" :key="`${showAllErrors}-${e.row}-${e.col}-${i}`">
              <td>{{ e.row ?? '-' }}</td>
              <td>{{ e.col ?? '-' }}</td>
              <td v-html="formatMessage(e.message)"></td>
            </tr>
            </tbody>
          </v-simple-table>

          <div v-if="hasMoreErrors && !showAllErrors" class="mt-2 d-flex align-center">
            <span class="mr-2 caption">
              Всего ошибок: {{ errorCount }}
            </span>
            <v-btn small text @click="toggleErrors">
              {{ showAllErrors ? 'Свернуть' : `Показать все` }}
            </v-btn>
          </div>
        </div>
      </div>
    </v-card-text>

    <v-card-actions class="px-4 pt-2 pb-4">
      <v-spacer></v-spacer>
      <v-btn
          text
          outlined
          class="import-button"
          @click="$emit('close')"
      >
        Закрыть
      </v-btn>
      <v-btn class="import-button" color="primary" :disabled="!file" v-if="!result?.ok" @click="submit">
        Импортировать
      </v-btn>
    </v-card-actions>
    <LoaderDialog :show="loading" msg="Загрузка"></LoaderDialog>
  </v-card>
</template>

<script>
import api from "@/modules/api";
import LoaderDialog from "@/components/elements/LoaderDialog.vue";

export default {
  name: 'StandardImportDialog',
  components: {LoaderDialog},
  props: {
    report: null,
    config: null,
  },
  data: () => ({
    domain: 'standard_report',
    file: null,
    loading: false,
    result: null,
    showAllErrors: false,
    downloadTaskId: null,
  }),
  computed: {
    errorCount() {
      return this.result?.errors?.length || 0;
    },
    hasMoreErrors() {
      return this.errorCount > 5;
    },
    visibleErrors() {
      const errors = this.result?.errors || [];
      return this.showAllErrors ? errors : errors.slice(0, 5);
    },
  },
  methods: {
    onFileChange() {
      this.result = null;
      this.showAllErrors = false;
    },
    async submit() {
      if (!this.file || !this.report || !this.config) return;

      this.result = null;
      this.showAllErrors = false;

      const name = (this.file.name || '').toLowerCase();
      const dot = name.lastIndexOf('.');
      const ext = dot >= 0 ? name.slice(dot) : '';
      const allowed = ['.xlsx'];
      if (!allowed.includes(ext)) {
        this.result = {
          ok: false,
          message: 'Неверный формат файла. Поддерживается только .xlsx',
        };
        return;
      }

      this.loading = true;
      try {
        const form = new FormData();
        form.append("file", this.file);

        const path = `/reports/import/${this.domain}`;
        const querySpec = {
          config_id: this.config.id,
          indicator_form_id: this.report.id,
        };

        const { ok, payload, errorJson } = await api.postFile(path, querySpec, form, "POST");
        this.result = ok
            ? (payload || { ok: true, message: "Импорт выполнен" })
            : (errorJson || { ok: false, message: "Ошибка при импорте файла" });
      } catch (e) {
        this.result = { ok: false, message: "Ошибка при импорте файла" };
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async exportTemplate() {
      try {
        this.loading = true
        const response = (await api.get(`/reports/generate/${this.domain}/2?config_id=${this.config.id}&format=xlsx`)).payload
        this.downloadTaskId = response.task_id
        await this.checkTaskStatus()
      } catch (error) {
        console.error('Error generating report:', error)
        alert('Ошибка при генерации отчета!')
        this.loading = false
      }
    },
    async checkTaskStatus() {
      try {
        const response = (await api.get(`/reports/status/${this.downloadTaskId}`)).payload
        if (response.status === 'SUCCESS') {
          await this.downloadFile()
        } else {
          setTimeout(this.checkTaskStatus, 2000)
        }
      } catch (error) {
        console.error('Error checking task status:', error)
        alert('Ошибка при проверке статуса задачи!')
        this.loading = false
      }
    },
    async downloadFile() {
      try {
        const blob = (await api.get(`/reports/download/${this.downloadTaskId}`, null, 'blob')).payload
        await this.$downloadBlob(blob, `${this.config.title}.xlsx`)
      } catch (error) {
        console.error('Error downloading file:', error)
        alert('Ошибка при скачивании файла!')
      } finally {
        this.loading = false
      }
    },
    formatMessage(msg) {
      if (!msg) return '';
      let html = msg.replace(/\[(.+?)\]/g, '<b>$1</b>');
      html = html.replace(/\n/g, '<br>');
      return html;
    },
    toggleErrors() {
      this.showAllErrors = !this.showAllErrors;
    },
  },
}
</script>

<style lang="scss" scoped>
.import-button {
  height: 45px !important;
}
.import-instructions {
  .instruction-list {
    margin: 0;
    padding-left: 1.2rem;

    li {
      margin-bottom: .4rem;
      line-height: 1.4;
    }
  }
}
</style>
